import { useMemo } from 'react';
import { IMG_MODELS, VID_MODELS, EDIT_TYPES, RES_MULT, DUR_MULT } from '../constants/models';

export function useCostCalculator({
  mode,          // 'image' | 'video' | 'edit' | 'frame2video' | 'lip-sync'
  model,         // image model id
  vidModel,      // video model id
  videoMode,     // 'Fast' | 'Quality' | 'Master' (for Kling 2.1)
  resolution,    // '720p' | '1080p'
  duration,      // '5s' | '6s' | '8s' | '12s' OR numeric seconds for lip-sync
  audioOn,       // boolean
  numVideos,     // 1-4
  numImages,     // 1-4 (for image mode)
  shots,         // array of shot objects
  editType,      // edit type id
  lipSyncModel,  // lip-sync model id
  lipSyncMode,   // lip-sync resolution/mode
  hasVideoInput, // boolean
  quality,       // 'low' | 'medium' | 'high' (for models with hasQuality)
  aspectRatio,   // '16:9' | '9:16' | '1:1' etc. (needed for GPT Image 2 high pricing)
}) {
  const cost = useMemo(() => {
    if (mode === 'lip-sync') {
      const durSec = typeof duration === 'number' ? duration : (parseInt(duration) || 0);
      let rate = 15; // default

      if (lipSyncModel === 'kling-avatar') {
        rate = lipSyncMode === 'pro' ? 40 : 30;
      } else if (lipSyncModel === 'meigen-ai-infinitetalk') {
        rate = lipSyncMode === '720p' ? 40 : 25;
      }

      return Math.ceil(durSec * rate);
    }

    if (mode === 'video' || mode === 'frame2video') {
      const vm = VID_MODELS.find(m => m.id === vidModel);
      if (!vm) return 0;

      // Custom pricing for Kling 2.1
      if (vidModel === 'kling-2.1') {
        let c = 0;
        const is10s = duration === '10s';

        if (mode === 'video') {
          // Text to Video
          c = is10s ? 700 : 350;
        } else {
          // Frame to Video
          if (videoMode === 'Master') {
            c = is10s ? 700 : 350;
          } else if (videoMode === 'Pro') {
            c = is10s ? 280 : 140;
          } else {
            // Standard
            c = is10s ? 150 : 80;
          }
        }
        return c * Math.min(numVideos || 1, 4);
      }

      // Custom pricing for Wan 2.7
      if (vidModel === 'wan-2.7') {
        const durSec = parseInt(duration) || 10;
        const rate = resolution === '1080p' ? 55 : 40;
        return (durSec * rate) * Math.min(numVideos || 1, 4);
      }

      // Kling 3.0
      if (vidModel === 'kling-3') {
        const durSec = parseInt(duration) || 5;
        let rate = 35; // Standard, No Audio
        if (resolution === '1080p') {
          rate = audioOn ? 70 : 50;
        } else {
          rate = audioOn ? 55 : 35;
        }
        return (durSec * rate) * Math.min(numVideos || 1, 4);
      }

      // Custom pricing for Kling 2.6
      if (vidModel === 'kling-2.6') {
        let c = duration === '10s' ? 200 : 100;
        if (audioOn) c *= 2; // 200cr for 5s, 400cr for 10s
        return c * Math.min(numVideos || 1, 4);
      }

      // Seedance 2 & Fast — per second pricing (video input detected at submission)
      if (vidModel?.startsWith('seedance-2')) {
        const res = (resolution || '720p').toLowerCase();
        const durSec = parseInt(duration) || 5;
        let ratePerSec = 70;
        if (res === '480p') {
          ratePerSec = hasVideoInput ? 25 : 35;
        } else if (res === '1080p' && vidModel === 'seedance-2') {
          ratePerSec = hasVideoInput ? 90 : 140;
        } else {
          ratePerSec = hasVideoInput ? 45 : 70;
        }
        return ratePerSec * durSec;
      }

      let c = vm.baseCost;
      c *= (RES_MULT[resolution] || 1);
      c *= (DUR_MULT[duration] || 1);
      if (audioOn) c *= 1.3;
      const isMultiShot = vm.multiShot && mode === 'video';
      const shotCount = isMultiShot ? Math.min(shots?.length || 1, 5) : 1;
      if (isMultiShot && shotCount > 1) {
        c += (shotCount - 1) * vm.baseCost * 0.6;
      }
      return Math.ceil(c) * Math.min(numVideos || 1, 4);
    }

    if (mode === 'edit') {
      return EDIT_TYPES.find(e => e.id === editType)?.cost || 3;
    }

    // image
    const imgModel = IMG_MODELS.find(m => m.id === model);
    if (!imgModel) return 0;

    let singleCost;
    if (model === 'gpt-image-2' && imgModel.qualityCosts) {
      const q = (quality || 'medium').toLowerCase();
      if (q === 'high') {
        const wideRatios = ['16:9', '9:16'];
        singleCost = wideRatios.includes(aspectRatio) ? imgModel.qualityCostWide : imgModel.qualityCostOther;
      } else {
        singleCost = imgModel.qualityCosts[q] ?? imgModel.qualityCosts.medium;
      }
    } else if (imgModel.resPrices && imgModel.resPrices[resolution || '1k']) {
      // Models with explicit per-resolution pricing (Nano Banana, Flux 2, etc.)
      singleCost = imgModel.resPrices[resolution || '1k'];
    } else {
      // Fallback: base cost × resolution multiplier
      const DEFAULT_RES_MULT = { '1k': 1, '2k': 1.5, '3k': 2.0, '4k': 2.5 };
      const mult = (imgModel.resMult || DEFAULT_RES_MULT)[resolution || '1k'] || 1;
      singleCost = Math.ceil((imgModel.cost || 3) * mult);
    }

    return singleCost * Math.max(numImages || 1, 1);
  }, [mode, model, vidModel, videoMode, resolution, duration, audioOn, numVideos, numImages, shots, editType, lipSyncModel, lipSyncMode, hasVideoInput, quality, aspectRatio]);

  return cost;
}
